import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { toast } from "sonner";
import { Trash2, Mail, Download } from "lucide-react";

export default function AdminWaitlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/waitlist");
      setRows(data.entries);
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Retirer ${email} de la liste ?`)) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setRows((r) => r.filter((e) => e.id !== id));
      toast.success("Retiré");
    } catch {
      toast.error("Échec");
    }
  };

  const exportCsv = () => {
    const lines = ["email,service,source,created_at"];
    rows.forEach((r) =>
      lines.push([r.email, r.service, r.source || "", r.created_at].join(",")),
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-dassimaoauto-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-waitlist-page">
      <AdminHeader />
      <main className="max-w-6xl mx-auto pt-28 pb-16 px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Marketing</div>
            <h1 className="mt-2 font-serif text-white text-4xl">Liste d'attente</h1>
            <p className="mt-2 text-gray-400 text-sm">
              Emails collectés sur la page Dépannage — à contacter dès l'ouverture du service.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="waitlist-export-btn"
              disabled={rows.length === 0}
              onClick={exportCsv}
              className="btn-outline-gold inline-flex items-center gap-2 text-sm disabled:opacity-40"
            >
              <Download size={14} /> Export CSV
            </button>
            <a
              href={`mailto:?bcc=${rows.map((r) => r.email).join(",")}`}
              data-testid="waitlist-mail-all"
              className={`btn-gold inline-flex items-center gap-2 text-sm ${rows.length === 0 ? "pointer-events-none opacity-40" : ""}`}
            >
              <Mail size={14} /> Écrire à tous
            </a>
          </div>
        </div>

        <div className="mt-10 border border-white/10 bg-[#0a0a0a] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-waitlist-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-gray-500">
                <th className="p-4">Email</th>
                <th className="p-4">Service</th>
                <th className="p-4">Source</th>
                <th className="p-4">Inscrit le</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">Chargement...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">
                    Aucune inscription pour le moment.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4 text-white" data-testid={`waitlist-row-${r.email}`}>
                    <a href={`mailto:${r.email}`} className="hover:gold">
                      {r.email}
                    </a>
                  </td>
                  <td className="p-4 text-gray-300 capitalize">{r.service}</td>
                  <td className="p-4 text-gray-500 text-xs">{r.source || "—"}</td>
                  <td className="p-4 text-gray-500 text-xs">
                    {new Date(r.created_at).toLocaleString("fr-FR")}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      data-testid={`waitlist-remove-${r.email}`}
                      onClick={() => remove(r.id, r.email)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Retirer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
